import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';

// Pages
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import RenewalsPage from './pages/admin/RenewalsPage';
import ClaimsPage from './pages/claims/ClaimsPage';
import NewClaimPage from './pages/claims/NewClaimPage';
import ClaimDetailPage from './pages/claims/ClaimDetailPage';
import PatientsPage from './pages/patients/PatientsPage';
import PayersPage from './pages/payers/PayersPage';
import DisplayBoardPage from './pages/display-board/DisplayBoardPage';
import TenantsPage from './pages/tenants/TenantsPage';
import PlansPage from './pages/plans/PlansPage';
import UsersPage from './pages/users/UsersPage';
import PreAuthPage from './pages/preauth/PreAuthPage';
import DenialsPage from './pages/denials/DenialsPage';
import ARFollowupPage from './pages/ar-followup/ARFollowupPage';
import PaymentsPage from './pages/payments/PaymentsPage';
import ReportsPage from './pages/reports/ReportsPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import BranchesPage from './pages/branches/BranchesPage';
import NumberingConfigPage from './pages/settings/NumberingConfigPage';
import AlertsPage from './pages/alerts/AlertsPage';
import AuditLogsPage from './pages/audit/AuditLogsPage';
import WorkflowPage from './pages/workflow/WorkflowPage';
import SubscriptionPage from './pages/subscription/SubscriptionPage';
import HospitalUsersPage from './pages/hospital-users/HospitalUsersPage';
import SettingsPage from './pages/settings/SettingsPage';
import ExecutiveDashboardPage from './pages/executive/ExecutiveDashboardPage';
import SecurityDashboardPage from './pages/security/SecurityDashboardPage';
import PoliciesPage from './pages/policies/PoliciesPage';
import ABDMPage from './pages/abdm/ABDMPage';
import ABDMAdminPage from './pages/abdm/ABDMAdminPage';
import TicketsPage from './pages/tickets/TicketsPage';
import NewTicketPage from './pages/tickets/NewTicketPage';
import TicketDetailPage from './pages/tickets/TicketDetailPage';
import TicketDashboardPage from './pages/tickets/TicketDashboardPage';
import TicketConfigPage from './pages/tickets/TicketConfigPage';
import TicketAdminPage from './pages/tickets/TicketAdminPage';
import TicketReportsPage from './pages/tickets/TicketReportsPage';
import PlatformUsersPage from './pages/admin/PlatformUsersPage';
import EmailDashboardPage from './pages/emails/EmailDashboardPage';
import AIAnalyticsPage from './pages/analytics/AIAnalyticsPage';
import ApprovalsPage from './pages/approvals/ApprovalsPage';
import TPAPortalPage from './pages/tpa/TPAPortalPage';

import './App.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes */}
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DashboardLayout>
                  <SuperAdminDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/renewals"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DashboardLayout>
                  <RenewalsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/security"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <SecurityDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/policies"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <PoliciesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/abdm"
            element={
              <ProtectedRoute allowedRoles={['hospital_admin', 'claim_manager', 'billing_executive']}>
                <DashboardLayout>
                  <ABDMPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/abdm-admin"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DashboardLayout>
                  <ABDMAdminPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TicketsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets/new"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <NewTicketPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TicketDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets/config"
            element={
              <ProtectedRoute allowedRoles={['hospital_admin']}>
                <DashboardLayout>
                  <TicketConfigPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets/reports"
            element={
              <ProtectedRoute allowedRoles={['hospital_admin', 'claim_manager', 'finance_manager']}>
                <DashboardLayout>
                  <TicketReportsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets-admin"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DashboardLayout>
                  <TicketAdminPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/platform-users"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DashboardLayout>
                  <PlatformUsersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/email-center"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EmailDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai-analytics"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AIAnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/approvals"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ApprovalsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tpa-portal"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <TPAPortalPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tickets/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TicketDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/executive-dashboard"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin', 'finance_manager']}>
                <DashboardLayout>
                  <ExecutiveDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/claims"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ClaimsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/claims/new"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <NewClaimPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/claims/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ClaimDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/preauths"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PreAuthPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/denials"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DenialsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ar-followup"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ARFollowupPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/patients"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PatientsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/payers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PayersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/display-board"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DisplayBoardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PaymentsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ReportsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <NotificationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/tenants"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DashboardLayout>
                  <TenantsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/plans"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DashboardLayout>
                  <PlansPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <UsersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/branches"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <BranchesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/numbering-config"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <NumberingConfigPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/alerts"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AlertsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/workflow"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin', 'claim_manager']}>
                <DashboardLayout>
                  <WorkflowPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute allowedRoles={['hospital_admin']}>
                <DashboardLayout>
                  <AuditLogsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscription"
            element={
              <ProtectedRoute allowedRoles={['hospital_admin']}>
                <DashboardLayout>
                  <SubscriptionPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/hospital-users"
            element={
              <ProtectedRoute allowedRoles={['hospital_admin']}>
                <DashboardLayout>
                  <HospitalUsersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'hospital_admin']}>
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Redirect root to appropriate dashboard */}
          <Route path="/" element={<SmartRedirect />} />
          
          {/* Catch all */}
          <Route path="*" element={<SmartRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Smart redirect based on role
function SmartRedirect() {
  const token = localStorage.getItem('access_token');
  if (!token) return <Navigate to="/login" replace />;
  
  // Check if user is super admin from stored data
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.role === 'super_admin') return <Navigate to="/admin-dashboard" replace />;
  } catch {}
  return <Navigate to="/dashboard" replace />;
}

export default App;
